import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Badge, MyLink } from '@scramble/ui';
import { STREAK_STATUSES, STREAK_STEP } from '@/helpers/constants';
import { APP_LINKS } from '@/helpers/links';
import IconChevronRight from '@scramble/design-system/icons/chevron-right.svg'
import { useGetInvestmentStreakStatus } from '@/query/queries/streak/useGetInvestmentStreakStatus';
import s from './index.module.scss';

const BADGE_TYPE_MAP = {
  [STREAK_STATUSES.active]: 'success',
  [STREAK_STATUSES.paused]: 'warning'
};

const getBadgeLabel = (status, t) => {
  switch (status) {
    case STREAK_STATUSES.active:
      return t('streak:streak_badge.active');
    case STREAK_STATUSES.paused:
      return t('streak:streak_badge.paused');
    case STREAK_STATUSES.broken:
      return t('streak:streak_badge.broken');
    case STREAK_STATUSES.not_started:
    default:
      return t('streak:streak_badge.not_started');
  }
};

export const InvestmentStreakStatus = () => {
  const { data: status } = useGetInvestmentStreakStatus();
  const { t } = useTranslation();

  if (!status) return null;

  const progressStreak =
    status.streak <= status.max_milestone
      ? status.streak
      : status.max_milestone;

  return (
    <MyLink href={APP_LINKS.investmentStreak} className={s.wrapper}>
      <h2 className={s.title}>
        {t('dashboard:dash_streak_status.your_current_streak')}
        <img src={IconChevronRight} alt="icon" width={28} height={28} />
      </h2>
      <div className={s.card}>
        <div className={s.header}>
          <div className={s.circle}>
            <div className={s.streak}>{status?.streak}</div>
            <Badge
              type={BADGE_TYPE_MAP[status?.status] || 'neutral'}
              appearance="filled"
              label={getBadgeLabel(status?.status, t)}
              className={s.badge}
              size="small"
            />
          </div>
          <div className={s.content}>
            <div className="f-body-md f-500">{status.title}</div>
            <div className="f-body-sm">{status.description}</div>
          </div>
        </div>
        <div className={s.progress}>
          <div className={s.segments}>
            {new Array(STREAK_STEP).fill('').map((_, i) => (
              <div
                key={i}
                className={classnames(
                  s.segment,
                  (i + 1 <= progressStreak % 3 ||
                    progressStreak === status.current_milestone) &&
                    s.segment_filled,
                )}
              />
            ))}
          </div>
          <div className={s.bottom}>
            <div className="f-body-sm">
              {t('dashboard:dash_streak_status.milestone_progress')}
            </div>
            <div className="f-body-sm f-500">
              {t('dashboard:dash_streak_status.invested_in_rounds', {
                streak: progressStreak,
                milestone: status.current_milestone,
              })}
            </div>
          </div>
        </div>
      </div>
    </MyLink>
  );
};
